/**
 * SearchablePicker — a combobox for choosing one record from a searchable set.
 *
 * Lifted out of apps/admin-oneup-web/src/components/record-picker.tsx (which
 * still owns the app-specific RECORD_SOURCES map) so every portal links records
 * the same way instead of growing its own picker. Same migration the workspace
 * primitives in record-workspace.tsx went through.
 *
 * Generic over its search source: callers supply `(query) => Promise<Option[]>`.
 * The source is called with an empty query when the popover first opens, so a
 * picker is always browsable even when the user has nothing to type — that is
 * the difference between "no suggestions" and "no way to proceed".
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Search, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../lib/utils';

/** Minimal value debounce — keeps the input responsive while throttling queries. */
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export interface RecordOption {
  id: string;
  label: string;
  sublabel?: string;
}

export type RecordSource = (query: string) => Promise<RecordOption[]>;

export interface SearchablePickerProps {
  /** Cache key segment identifying the search source (an entity/record type). */
  entityType: string;
  source: RecordSource;
  value: string;
  valueLabel?: string;
  onSelect: (id: string, label: string) => void;
  onClear: () => void;
  className?: string;
  placeholder?: string;
  /** Extra cache key segment, e.g. the client a search is scoped to. */
  scopeKey?: string;
  /** Message when the source returns nothing. */
  emptyLabel?: string;
}

export function SearchablePicker({
  entityType,
  source,
  value,
  valueLabel,
  onSelect,
  onClear,
  className,
  placeholder,
  scopeKey,
  emptyLabel = 'No records match.',
}: SearchablePickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debounced = useDebouncedValue(search, 250);

  const { data, isLoading } = useQuery({
    queryKey: ['record-picker', entityType, scopeKey ?? '', debounced],
    queryFn: () => source(debounced.trim()),
    enabled: open,
    staleTime: 60_000,
  });

  const options = useMemo(() => (data ?? []).slice(0, 80), [data]);
  const display = valueLabel || value;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={cn('relative w-full', className)}>
        <PopoverTrigger asChild>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className={cn(
              'flex h-9 w-full items-center justify-between gap-1.5 rounded-md border border-border bg-card px-2.5 text-left text-sm transition-colors hover:border-primary/40',
              display ? 'pr-7' : 'pr-2.5',
            )}
          >
            <span className={cn('truncate', display ? 'text-foreground' : 'text-muted-foreground')}>
              {display || placeholder || 'Search records…'}
            </span>
            {!display && (
              <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" aria-hidden />
            )}
          </button>
        </PopoverTrigger>
        {display && (
          <button
            type="button"
            aria-label="Clear selection"
            onClick={(event) => {
              event.stopPropagation();
              onClear();
            }}
            className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-3 w-3" aria-hidden />
          </button>
        )}
      </div>
      <PopoverContent className="w-[320px] p-0" align="start">
        <div className="relative border-b border-border">
          <Search
            className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search…"
            autoFocus
            className="h-9 w-full bg-transparent pl-7 pr-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        </div>
        <ul className="max-h-[280px] overflow-y-auto py-1">
          {isLoading && <li className="px-3 py-2 text-xs text-muted-foreground">Loading…</li>}
          {!isLoading && options.length === 0 && (
            <li className="px-3 py-2 text-xs text-muted-foreground">{emptyLabel}</li>
          )}
          {options.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                onClick={() => {
                  onSelect(option.id, option.label);
                  setOpen(false);
                  setSearch('');
                }}
                className={cn(
                  'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-muted focus-visible:bg-muted',
                  value === option.id && 'bg-selection text-foreground',
                )}
              >
                <span className="truncate">{option.label}</span>
                {option.sublabel && (
                  <span className="ml-2 flex-shrink-0 text-[11px] text-muted-foreground">
                    {option.sublabel}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
